import { ITEMS_KEYS, UI } from "@/constants";
import { ItemType, NavType, OrderType, SearchParams, WeekGroup } from "@/types";
import { clsx, type ClassValue } from "clsx";
import { format, isWithinInterval, subMonths } from "date-fns";
import { DateRange } from "react-day-picker";
import { twMerge } from "tailwind-merge";

export const getSeasonBounds = (baseDate: Date) => {
  const y = baseDate.getFullYear();
  const seasonStart = new Date(y, 5, 1);
  const seasonEnd = new Date(y, 10, 30);
  return { seasonStart, seasonEnd };
};

const toYmd = (d: Date) => format(d, "yyyy-MM-dd");

export const getDefaultParams = (now: Date = new Date()) => {
  const thisSeason = getSeasonBounds(now);

  const inSeason = isWithinInterval(now, {
    start: thisSeason.seasonStart,
    end: thisSeason.seasonEnd,
  });

  const endDate = inSeason
    ? now
    : now < thisSeason.seasonStart
      ? getSeasonBounds(new Date(now.getFullYear() - 1, 6, 1)).seasonEnd
      : thisSeason.seasonEnd;

  const startDate = subMonths(endDate, 1);

  return {
    size: "10",
    date_from: toYmd(startDate),
    date_to: toYmd(endDate),
  };
};

const DEFAULT_PARAMS = getDefaultParams();

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const range = (n: number) => [...Array(n).keys()];

export const formatDate = (value: string, year: boolean = true) => {
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: year ? "numeric" : undefined,
  });
};

export const getWeekNumber = (week: number): string => {
  const suffix =
    week % 10 === 1 && week % 100 !== 11 ? "st" :
    week % 10 === 2 && week % 100 !== 12 ? "nd" :
    week % 10 === 3 && week % 100 !== 13 ? "rd" : "th";
  return `${week}${suffix}`;
};

export const formatDateRange = (dataRange: DateRange) => {
  return {
    date_from: dataRange.from ? format(dataRange.from, "yyyy-MM-dd") : undefined,
    date_to: dataRange.to ? format(dataRange.to, "yyyy-MM-dd") : undefined,
  };
};

type defaultParamsProps = {
  searchParams: Promise<SearchParams>;
  extraParams?: Partial<SearchParams>;
};

export const defaultParams = async ({ extraParams, searchParams }: defaultParamsProps) => {
  const sp = await searchParams;
  const params: SearchParams = {
    size: sp.size || DEFAULT_PARAMS.size,
    date_from: sp.date_from || DEFAULT_PARAMS.date_from,
    date_to: sp.date_to || DEFAULT_PARAMS.date_to,
    user_id: sp.user_id || undefined,
    product_id: sp.product_id || undefined,
    ...extraParams,
  };
  if(sp.sort_by) params.sort_by = sp.sort_by;
  if(sp.sort_dir) params.sort_dir = sp.sort_dir;
  return params;
};

export const findKey = (key: string, keys: string[]) => keys.includes(key);

export const formatCurrency = (key: string, value: number | string) => {
  if (!value) return null;
  return key === "price" ? `$${Number(value).toFixed(2)}` : value;
};

type ItemStylesProps = {
  itemsKey: (typeof ITEMS_KEYS)[number];
  delta: ItemType["delta"];
  count: ItemType["count"];
};

export const itemStatus = ({ itemsKey, delta, count }: ItemStylesProps) => {
  switch (itemsKey) {
    case "defaultShareItems":
      if (delta > 0) return "totallyAdded";
      if (delta === 0) return "noChanges";
      if (delta < 0 && Math.abs(delta) < count) return "partiallyRemoved";
      if (delta < 0 && Math.abs(delta) === count) return "totallyRemoved";
    case "deliveredItems":
      if (delta > 0 && Math.abs(delta) === count) return "totallyAdded";
      if (delta > 0 && Math.abs(delta) < count) return "partiallyAdded";
      if (delta === 0) return "noChanges";
      if (delta < 0) return "partiallyRemoved";
    default:
      return "noChanges";
  }
};

export const productStatus = (delta: number) => {
  if (delta > 0) return "totallyAdded";
  if (delta < 0) return "totallyRemoved";
  return "noChanges";
};

export const omitKeys = <T extends object, K extends keyof T>(arr: T[], keys: readonly K[]) => {
  const omit = new Set<keyof T>(keys);
  return arr.map(
    o =>
      Object.fromEntries(Object.entries(o).filter(([k]) => !omit.has(k as keyof T))) as Omit<T, K>,
  );
};

const findNavByKey = (navArr: NavType[], key: NavType["key"]): NavType | undefined => {
  if (!navArr?.length) return undefined;
  for (const nav of navArr) {
    if (nav.key === key) return nav;
    if (nav.subMenu) {
      const found = findNavByKey(nav.subMenu, key);
      if (found) return found;
    }
  }
  return undefined;
};

export const getNavByKey = (key: NavType["key"]) => {
  if (!UI.navigation?.length) {
    throw new Error(UI.errors.navError(key || "undefined"));
  }
  return findNavByKey(UI.navigation, key);
};

export const timeFormat = (sec: number) => {
  if (!sec) return;
  const { h, m, s } = UI.labels;
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor(sec / 60);
  const seconds = sec % 60;

  const time = [
    hours ? `${hours}${h}` : null,
    minutes ? `${minutes}${m}` : null,
    `${seconds.toFixed()}${s}`,
  ]

  return time.filter(Boolean).join(" ");
};

export const getRoundedMax100 = (a: number, b: number) => {
  const max = Math.max(a, b);
  return Math.ceil(max / 100) * 100;
};

type FormatOpts = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

export const numberUsLocale = (value: number, opts: FormatOpts = {}): string => {
  if (!Number.isFinite(value)) return String(value);

  return new Intl.NumberFormat("en-US", {
    useGrouping: true,
    minimumFractionDigits: opts.minimumFractionDigits,
    maximumFractionDigits: opts.maximumFractionDigits,
  }).format(value);
}

export const groupByWeek = (orders: OrderType[]) => {
  const map = new Map<number, WeekGroup>();

  for (const o of orders) {
    const prev = map.get(o.weekNumber);

    if (!prev) {
      map.set(o.weekNumber, {
        weekNumber: o.weekNumber,
        productsAdded: o.productsAdded,
        productsRemoved: o.productsRemoved,
        ordersCount: o.ordersCount || 0,
      });
      continue;
    }

    prev.productsAdded += o.productsAdded;
    prev.productsRemoved += o.productsRemoved;
    prev.ordersCount += o.ordersCount || 0;
  }

  return [...map.values()].sort((a, b) => a.weekNumber - b.weekNumber);
}

export const parseYmdLocal = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};